import {
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import type { ComponentType } from 'react';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import {
  BellIcon,
  DocumentCleanIcon,
  GridIcon,
  HomeIcon,
  LinkIcon,
  MessageCircleDotsIcon,
} from '../../components/icons';
import { useAdminSections } from '../admin/useAdminSections';
import { SidebarLogo } from './SidebarLogo';
import { SidebarSearchTrigger } from './SidebarSearchTrigger';
import { SidebarSectionLabel } from './SidebarSectionLabel';
import { SidebarUserFooter } from './SidebarUserFooter';
import { YourTeamSidebarItem } from './YourTeamSidebarItem';

// Nav item id -> inline Hone stroke icon component, replacing each plugin's
// default MUI icon (PortalSidebar.dc.html specifies the custom set). Items
// without an entry keep their plugin-provided icon.
const NAV_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  'page:home': HomeIcon,
  'page:catalog': GridIcon,
  'page:techdocs': DocumentCleanIcon,
  'page:api-docs': LinkIcon,
  'page:announcements': BellIcon,
};

// The vendor api-docs page titles itself "APIs"; the nav reads better as
// "API Explorer" to match the home page's Start Here card.
const NAV_TEXT: Record<string, string> = {
  'page:api-docs': 'API Explorer',
};

const AdminSettingsSidebarItem = () => {
  const { sections, loading } = useAdminSections();

  if (loading || sections.length === 0) {
    return null;
  }

  return (
    <SidebarItem
      icon={SupervisorAccountIcon}
      to="/admin-settings"
      text="Admin Settings"
    />
  );
};

const SidebarContent = NavContentBlueprint.make({
  params: {
    component: function SidebarNav({ navItems }) {
      const nav = navItems.withComponent(item => {
        const CustomIcon = NAV_ICONS[item.node.spec.id];
        return (
          <SidebarItem
            icon={
              CustomIcon
                ? () => <CustomIcon className="hone-nav-icon" />
                : () => item.icon
            }
            to={item.href}
            text={NAV_TEXT[item.node.spec.id] ?? item.title}
            end={item.node.spec.id === 'page:catalog'}
          />
        );
      });

      [
        'page:search',
        'page:catalog-graph',
        'page:notifications',
        'page:catalog-import',
        'page:app/feedback',
        'page:feedback',
        'page:app/admin-feedback',
        'page:admin-feedback',
        'page:app/pagerduty',
        'page:pagerduty',
        'page:app/rbac',
        'page:rbac',
        'page:app/slack-channels',
        'page:slack-channels',
        'page:app/admin-settings',
        'page:admin-settings',
        'page:user-settings',
        // Redirects for the pre-rename /docs paths — link-only, never nav items.
        'page:techdocs/docs-redirect',
        'page:techdocs/docs-reader-redirect',
      ].forEach(id => nav.take(id));

      const home = nav.take('page:home');
      const catalog = nav.take('page:catalog');
      const techdocs = nav.take('page:techdocs');
      const apiDocs = nav.take('page:api-docs');
      const announcements = nav.take('page:announcements');
      const incidents = nav.take('page:app/incidents');

      return (
        <Sidebar sidebarOptions={{ drawerWidthOpen: 264 }}>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchTrigger />
          </SidebarGroup>
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {home}
            {techdocs}
            {catalog}
            {apiDocs}
            <SidebarSectionLabel label="Company" />
            <YourTeamSidebarItem />
            {announcements}
            <SidebarItem
              icon={() => <MessageCircleDotsIcon className="hone-nav-icon" />}
              to="/feedback"
              text="Feedback"
            />
            {incidents}
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <NotificationsSidebarItem />
          <AdminSettingsSidebarItem />
          <SidebarUserFooter />
        </Sidebar>
      );
    },
  },
});

export const navModule = createFrontendModule({
  pluginId: 'app',
  extensions: [SidebarContent],
});
